// React/finance-app/src/features/tt-insights/tabs/SummaryTab.jsx
import React from 'react';
import * as UI from '../components';
import ExportButton from '../ExportButton';
import { fmtMoney } from '../utils';

const common = UI.commonStyles || {
  spacing: { md: '1rem', lg: '1.5rem' },
  colors: {
    primary: '#0d6efd',
    success: '#198754',
    muted: '#6c757d',
    light: '#f8f9fa',
    dark: '#212529',
  },
};

// ---------- light UI fallbacks ----------
const Card =
  UI.Card ||
  (({ title, icon, children, footer, style }) => (
    <div
      style={{
        background: '#fff',
        border: '1px solid #eee',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        ...style,
      }}
    >
      {(title || icon) && (
        <div style={{ fontWeight: 600, marginBottom: 12 }}>
          {icon} {title}
        </div>
      )}
      {children}
      {footer}
    </div>
  ));

const SectionHeader =
  UI.SectionHeader ||
  (({ icon, title, subtitle, action }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        borderBottom: '1px solid #eee',
        paddingBottom: 8,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          {icon} {title}
        </div>
        {subtitle && <div style={{ fontSize: 12, color: '#666' }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  ));

const Badge =
  UI.Badge ||
  (({ label, tone = 'success' }) => (
    <span
      style={{
        background: tone === 'success' ? '#198754' : '#6c757d',
        color: '#fff',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  ));

const LoadingState =
  UI.LoadingState ||
  (({ message }) => <div style={{ padding: 16 }}>{message || 'Loading...'}</div>);

const EmptyState =
  UI.EmptyState ||
  (({ title, description }) => (
    <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12 }}>{description}</div>
    </div>
  ));

const KeyValueGrid = UI.KeyValueGrid;

// ---------- tiny helpers ----------
const StatCard = ({ icon, label, value, hint }) => (
  <Card
    style={{
      marginBottom: 0,
      padding: 14,
      borderRadius: 10,
      boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
        {hint && <div style={{ fontSize: 11, color: '#8a8f98', marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  </Card>
);

const Row = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      padding: '6px 0',
      fontSize: 13,
      borderBottom: '1px dashed #f0f0f0',
    }}
  >
    <span style={{ color: common.colors.muted }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

// ---------- main ----------
const SummaryTab = ({ loading, summary, userCode }) => {
  const hasData = summary && typeof summary === 'object' && Object.keys(summary).length > 0;

  if (loading) return <LoadingState message="Loading summary..." />;

  if (!hasData) {
    return (
      <div style={{ padding: common.spacing.lg }}>
        <EmptyState
          title="No Summary"
          description="Enter a user code in the controls above and click “Get Summary”."
        />
      </div>
    );
  }

  // Robust pulls for slightly different key names we’ve seen
  const totalRebatesRaw =
    summary.total_rebate ??
    summary.total_rebates ??
    summary.total_rebate_total ??
    0;

  const portfolioValueRaw =
    summary.net_portfolio_value ??
    summary.gross_portfolio_value ??
    0;

  const availableBalanceRaw = summary.available_balance ?? 0;

  // Extra portfolio/balance context if present
  const unsold = 
    summary.total_cost_of_unsold_tickets ?? 
    summary.unsold_value ?? 
    summary.unsold_total_value ?? 
    null;
  const pendingPay = summary.pending_payments ?? null;

  // Build "known keys" so Raw Summary only shows the rest
  const knownKeys = new Set([
    'total_rebate',
    'total_rebates',
    'total_rebate_total',
    'gross_portfolio_value',
    'net_portfolio_value',
    'available_balance',
    'total_cost_of_unsold_tickets',
    'unsold_value',
    'unsold_total_value',
    'pending_payments',
    'pending_referral',
    'paid_referral_rebate',
    'paid_referral',
    'pending_trust',
    'paid_trust_rebate',
    'paid_trust',
    'pending_deposit',
    'paid_deposit_rebate',
    'paid_deposit',
    'total_paid',
    'charges_and_credit',
    'charges_and_credits',
    'withdraws',
    'withdrawn',
  ]);

  const leftovers = Object.fromEntries(
    Object.entries(summary).filter(([k]) => !knownKeys.has(k))
  );

  return (
    <div style={{ padding: common.spacing.lg }}>
      <SectionHeader
        icon="👤"
        title="User Summary"
        subtitle={`User Code: ${userCode || 'N/A'}`}
        action={<Badge label="Active" />}
      />

      {/* Top KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ gridColumn: 'span 4' }}>
          <StatCard icon="💰" label="Total Rebates" value={fmtMoney(totalRebatesRaw)} />
        </div>
        <div style={{ gridColumn: 'span 4' }}>
          <StatCard
            icon="📦"
            label="Net Portfolio Value"
            value={fmtMoney(portfolioValueRaw)}
            hint={unsold != null ? `Unsold value: ${fmtMoney(unsold)}` : undefined}
          />
        </div>
        <div style={{ gridColumn: 'span 4' }}>
          <StatCard
            icon="🏦"
            label="Available Balance"
            value={fmtMoney(availableBalanceRaw)}
            hint={pendingPay != null ? `Pending payments: ${fmtMoney(pendingPay)}` : undefined}
          />
        </div>
      </div>

      {/* Detail grid – two columns on wide screens, stacks on narrow */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        {/* Portfolio & Balances */}
        <Card title="📊 Portfolio & Balances">
          <Row label="Net Portfolio Value" value={fmtMoney(portfolioValueRaw)} />
          <Row label="Unsold Value" value={fmtMoney(unsold)} />
          <Row label="Pending Payments" value={fmtMoney(pendingPay)} />
          <Row label="Available Balance" value={fmtMoney(availableBalanceRaw)} />
        </Card>

        {/* Deposits */}
        <Card title="💳 Deposits">
          <Row label="Pending Deposit" value={fmtMoney(summary.pending_deposit)} />
          <Row 
            label="Paid Deposit" 
            value={fmtMoney(summary.paid_deposit_rebate ?? summary.paid_deposit)} 
          />
        </Card>

        {/* Referrals & Trust */}
        <Card title="🤝 Referrals & Trust">
          <Row label="Pending Referral" value={fmtMoney(summary.pending_referral)} />
          <Row 
            label="Paid Referral" 
            value={fmtMoney(summary.paid_referral_rebate ?? summary.paid_referral)} 
          />
          <Row label="Pending Trust" value={fmtMoney(summary.pending_trust)} />
          <Row 
            label="Paid Trust" 
            value={fmtMoney(summary.paid_trust_rebate ?? summary.paid_trust)} 
          />
        </Card>

        {/* Payments & Movements */}
        <Card title="📤 Payments & Movements">
          <Row label="Total Paid" value={fmtMoney(summary.total_paid)} />
          <Row
            label="Charges & Credit"
            value={fmtMoney(summary.charges_and_credits ?? summary.charges_and_credit)}
          />
          <Row label="Withdraws" value={fmtMoney(summary.withdrawn_new ?? summary.withdrawn ?? summary.withdraws)} />
        </Card>
      </div>

      {/* Raw block with export */}
      <Card
        title="📄 Raw Summary"
        icon="📁"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <ExportButton
              disabled={!hasData}
              filenameBase={`summary_${userCode || 'unknown'}`}
              sheetName="Summary"
              columns={[
                { key: 'key', header: 'Key' },
                { key: 'value', header: 'Value' },
              ]}
              rows={Object.entries(summary).map(([k, v]) => ({
                key: k,
                value: String(v),
              }))}
            />
          </div>
        }
      >
        {KeyValueGrid ? (
          <KeyValueGrid data={leftovers} />
        ) : (
          <pre
            style={{
              fontSize: 12,
              background: '#f8f9fa',
              padding: 12,
              borderRadius: 6,
              margin: 0,
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(leftovers, null, 2)}
          </pre>
        )}
      </Card>
    </div>
  );
};

export default SummaryTab;
